// Libs
import classNames from 'classnames/bind';
import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseCheckbox, BaseDynamicSelect, BaseInput, BaseLabel, BaseSelect, Loading } from '~/components/common';
// Others
import { defaultValuesTruckForm, schema } from './helper';
import {
  IBaseOption,
  IBodyUpdate,
  IPayloadCallApiFn,
  IQueryParamBase,
  IResultCallApiFn,
} from '~/utils/interface/common';
import {
  commonOptionStatus,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_STRING,
  optionRentalStatus,
  optionTrailerType,
  optionTruckType,
} from '~/utils/constants/common';
import { IFormTruck } from '~/utils/interface/truck';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch } from '~/utils/redux/hooks';
import { compareDataUpdate, removeEmptyObjects } from '~/utils/helper/common';
import { createTruck, getDetailTruck, updateTruck } from '~/thunks/truck/truckThunk';
import { getListTruckLocations } from '~/thunks/linxup/linxUpThunk';
// Styles, images, icons
import styles from './TruckForm.module.scss';
import { icons } from '~/assets';

type Props = {
  idTruck?: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const TruckFormModal = (props: Props) => {
  //#region Destructuring Props
  const { idTruck, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/truck', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    setValue,
    handleSubmit,
    getValues,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<IFormTruck>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesTruckForm,
  });

  const { isPairingCapability } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [initialData, setInitialData] = useState<IFormTruck>(defaultValuesTruckForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!idTruck) return;

    handleGetDetailTruck(idTruck);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTruck]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailTruck = (idTruck: string) => {
    setIsLoading(true);

    dispatch(getDetailTruck(idTruck))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, ...remainingData } = res?.data;
        const body: IFormTruck = {
          ...remainingData,
          name: remainingData?.name || EMPTY_STRING,
          truckType: remainingData?.truckType || EMPTY_STRING,
          rentalStatus: remainingData?.rentalStatus || EMPTY_STRING,
          status: remainingData?.status || EMPTY_STRING,
          note: remainingData?.note || EMPTY_STRING,
          numberOfSeats: remainingData?.numberOfSeats || EMPTY_STRING,
          linxupDeviceUuid: remainingData?.linxupDeviceUuid || EMPTY_STRING,
          isPairingCapability: remainingData?.isPairingCapability || false,
          trailerType: remainingData?.trailerType || [],
        };

        setInitialData(body);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFormTruck = (data: IFormTruck) => {
    if (idTruck) {
      handleEditTruck(data);
    } else {
      handleAddTruck(data);
    }

    setIsLoading(true);
  };

  const handleAddTruck = (data: IFormTruck) => {
    const convertData = removeEmptyObjects({
      ...data,
      trailerType: isPairingCapability ? data.trailerType : [],
    }) as IFormTruck;

    dispatch(createTruck(convertData))
      .unwrap()
      .then((res) => {
        handleCloseModal();
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleEditTruck = async (data: IFormTruck) => {
    const dataChange = compareDataUpdate(initialData, data) as IFormTruck;

    const convertData: IFormTruck = {
      ...dataChange,
      trailerType: isPairingCapability ? data.trailerType : [],
    };

    const params: IBodyUpdate<IFormTruck> = {
      id: idTruck || EMPTY_STRING,
      body: convertData,
    };

    dispatch(updateTruck(params))
      .unwrap()
      .then(() => {
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const fetchOptionsTruckType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionTruckType,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsRentalStatus = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionRentalStatus,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsStatus = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: commonOptionStatus,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchDataTrailerList = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionTrailerType,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsLocationLinxUp = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;
      const payloadParams: IQueryParamBase = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListTruckLocations(payloadParams)).unwrap();

        const formatOptions: IBaseOption[] = resp?.data.map((item) => ({
          label: item.personName,
          value: item.deviceUuid,
        }));

        const hasMore: boolean = false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );
  //#endregion Handle Function

  return (
    <Dialog
      id='truckFormModalComponent'
      aria-labelledby='truckFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='truckFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel label={t(idTruck ? 'form_add_truck_title_edit' : 'form_add_truck_title')} styleType='bigTitle' />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 columnSpacing={'24px'} rowSpacing={'24px'} container>
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'name'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('form_add_truck_title_name')}
                    placeholder={t('form_add_truck_name_placeholder')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.name?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'truckType'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_add_truck_title_truck_type')}
                    placeholder={t('form_add_truck_type_placeholder')}
                    callApiFn={fetchOptionsTruckType}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.truckType?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'rentalStatus'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_add_truck_rental_status')}
                    placeholder={t('form_add_truck_rental_status_placeholder')}
                    callApiFn={fetchOptionsRentalStatus}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.rentalStatus?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'status'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_add_truck_status')}
                    placeholder={t('form_add_truck_status_placeholder')}
                    callApiFn={fetchOptionsStatus}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.status?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'linxupDeviceUuid'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    isSearch
                    type='loadMore'
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_add_truck_title_related_linxup')}
                    placeholder={t('form_add_truck_device_placeholder')}
                    callApiFn={fetchOptionsLocationLinxUp}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.linxupDeviceUuid?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'numberOfSeats'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('form_add_truck_title_number_of_seats')}
                    placeholder={t('form_add_truck_number_of_seats_placeholder')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.numberOfSeats?.message}
                    type={'number'}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'note'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    multiline
                    rows={7}
                    height={'auto'}
                    label={t('form_add_truck_note')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.note?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'isPairingCapability'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseCheckbox
                    label={t('form_add_truck_title_is_pairing_capability')}
                    checked={value}
                    onChange={(value) => onChange(value)}
                  />
                );
              }}
            />
          </Grid2>

          {isPairingCapability && (
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'trailerType'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseDynamicSelect
                      multiple={true}
                      label={t('form_add_truck_title_trailer_type')}
                      callApiFn={fetchDataTrailerList}
                      value={value}
                      onChange={(selected) => {
                        setValue(
                          'trailerType',
                          selected.optionsSelected.map((opt) => opt.value)
                        );
                        onChange(selected.idSelected);
                      }}
                      errorMessage={errors?.trailerType?.message}
                    />
                  );
                }}
              />
            </Grid2>
          )}
        </Grid2>
      </DialogContent>

      {isLoading && <Loading />}

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormTruck)}
          disabled={idTruck ? !isDirty : false}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TruckFormModal;
